import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Button, Col, Form, PopoverBody, Row } from 'reactstrap';
import { ReduxState } from 'types';
import Spinner from '../../../components/Spinner';
import ConfirmAPI from '../../../utils/api/ConfirmAPI';
import { getFriendlyUserFacingErrorObjectAndMessage } from '../../../utils/util/util';
import ConfirmationDialogModal from '../Modals/ConfirmationDialogModal';
import AutoSaveInfoBox from './AutoSaveInfoBox';

const DEFAULT_RENDER_VALIDATION_ERRORS = (x) => x;

const ConfirmForm: FC<Props> = ({
  buttonIsBlock = true,
  renderValidationErrors = DEFAULT_RENDER_VALIDATION_ERRORS,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmationDialogIsOpen, setConfirmationDialogIsOpen] =
    useState(false);
  const toggleConfirmationDialog = useCallback(
    () => setConfirmationDialogIsOpen(!confirmationDialogIsOpen),
    [confirmationDialogIsOpen]
  );

  const propsObject = props.object;
  const propsPreSubmit = props.preSubmit;
  const propsCallback = props.callback;
  const propsDoNotSendOrganization = props.doNotSendOrganization;
  const propsCurrentOrganizationId = props.currentOrganizationId;
  const propsOnValidate = props.onValidate;
  const propsTransformObjectBeforeSubmit = props.transformObjectBeforeSubmit;
  const propsMethod = props.method;
  const propsUrl = props.url;
  const propsAction = props.action;
  const propsSubmitPromise = props.submitPromise;
  const propsShowErrorsAsToasts = props.showErrorsAsToasts;
  const propsRenderValidationErrors = renderValidationErrors;
  const propsDisabledHoverText = props.disabledHoverText;
  const propsOnIsSubmittingChange = props.onIsSubmittingChange;

  const preSubmit = useCallback(() => {
    // clear any previous message
    // @ts-expect-error
    setMessage(null);

    setIsSubmitting(true);
    if (typeof propsOnIsSubmittingChange !== 'undefined') {
      // @ts-expect-error
      propsOnIsSubmittingChange(true);
    }
    if (typeof propsPreSubmit !== 'undefined') {
      // @ts-expect-error
      propsPreSubmit();
    }
  }, [propsPreSubmit, propsOnIsSubmittingChange]);

  const onSubmitCallback = useCallback(
    (data, error, hardErrorMessage = null) => {
      // if there's an additional confirmation modal, close that regardless
      // of the result so it isn't stuck hanging
      setConfirmationDialogIsOpen(false);

      // failure; keep modal open
      if (error || hardErrorMessage) {
        const [errorObject, friendlyErrorMessage] =
          getFriendlyUserFacingErrorObjectAndMessage(error, hardErrorMessage);
        setMessage(friendlyErrorMessage);
        setIsError(true);
        if (typeof propsCallback !== 'undefined') {
          // @ts-expect-error
          propsCallback(null, errorObject, friendlyErrorMessage);
        }
      } else {
        // success; call back and toggle modal
        // @ts-expect-error
        setMessage(null);
        setIsError(false);
        if (typeof propsCallback !== 'undefined') {
          // @ts-expect-error
          propsCallback(data);
        }
      }

      // reset isSubmitting at the end (after the callback) to
      // avoid any possibility of a submitting button briefly enabling
      // before the callback is complete
      setIsSubmitting(false);
      if (typeof propsOnIsSubmittingChange !== 'undefined') {
        // @ts-expect-error
        propsOnIsSubmittingChange(false);
      }
    },
    [propsCallback, propsOnIsSubmittingChange]
  );

  const handleSubmit = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }

      // insert the user's current organization into the object to be sent
      const obj = {
        ...propsObject,
        ...(propsDoNotSendOrganization
          ? {}
          : { organization: propsCurrentOrganizationId }),
      };

      if (propsOnValidate) {
        // if validation fails, stop here
        if (!propsOnValidate(obj)) {
          return;
        }
      }

      const preparedObject = propsTransformObjectBeforeSubmit
        ? propsTransformObjectBeforeSubmit(obj)
        : obj;

      const method = propsMethod
        ? propsMethod
        : preparedObject.id
          ? 'PATCH'
          : 'POST';

      // if no url provided, this is a frontend-only person widget
      if (propsUrl) {
        const url =
          method === 'PATCH' || method === 'DELETE'
            ? propsUrl + (preparedObject.id ? '/' + preparedObject.id : '')
            : propsUrl;
        const object =
          method === 'PATCH' || method === 'DELETE'
            ? { ...preparedObject, id: undefined }
            : preparedObject;
        ConfirmAPI.sendRequestToConfirm(
          method,
          propsAction ? url + '/' + propsAction : url,
          object,
          onSubmitCallback,
          preSubmit
        );
      } else {
        // frontend-only
        // Still want to call preSubmit in case it relates to
        // state changes (ex. for updating warning dialogs)
        // NOTE: we call preSubmit here instead of propsPreSubmit
        // to ensure that appropriate state is set, e.g. the
        // loading indicator on the button
        preSubmit();

        // if a submit promise was passed in (to do some local async
        // work instead of via a url), call it)
        if (propsSubmitPromise) {
          propsSubmitPromise(preparedObject, onSubmitCallback);
        } else {
          // We use a promise here to simulate a mock API call, so that
          // React has the chance to update state changes (ex. related
          // to warning dialogs)
          // @ts-expect-error
          const mockAPICall = new Promise((resolve) => resolve());
          mockAPICall.then(() => {
            if (typeof propsCallback !== 'undefined') {
              // @ts-expect-error
              propsCallback(preparedObject);
            }
          });
        }
      }
    },
    [
      propsObject,
      propsDoNotSendOrganization,
      propsCurrentOrganizationId,
      propsOnValidate,
      propsTransformObjectBeforeSubmit,
      propsMethod,
      propsUrl,
      propsAction,
      propsSubmitPromise,
      onSubmitCallback,
      preSubmit,
      propsCallback,
    ]
  );

  const buttonClassName = props.buttonClassName ?? 'mt-4';

  // ensure this is generated only once to avoid The target '...' could not be identified in the dom errors
  const uniqueLocationString = useMemo(() => uniqueId('confirm-form-'), []);

  // if isSubmitting is provided externally, defer to that instead
  // of dictating it ourselves for controlling button enabling
  const finalIsSubmitting = props.isSubmitting ?? isSubmitting;

  const submitButton = useMemo(
    () => (
      <>
        <span
          id={uniqueLocationString + '-save-button'}
          className="d-flex align-items-center"
        >
          <Button
            autoFocus={props.autoFocusSubmitButton}
            className={buttonClassName}
            disabled={
              props.disabled ||
              finalIsSubmitting ||
              props.draftFetchState === 'LOADING'
                ? true
                : undefined
            }
            style={props.disabled ? { pointerEvents: 'none' } : undefined}
            color={props.buttonColor ? props.buttonColor : 'primary'}
            type={props.buttonType ? props.buttonType : 'submit'}
            block={buttonIsBlock}
            // if this is a non-submit button (used for forms within other
            // forms to avoid submitting the outer form), call handleSubmit
            // manually)
            onClick={
              props.buttonType && props.buttonType !== 'submit'
                ? handleSubmit
                : undefined
            }
          >
            {!finalIsSubmitting &&
              (props.submitText
                ? props.submitText
                : formatMessage({
                    id: 'app.views.widgets.forms.confirm_form.submit',
                    defaultMessage: 'Submit',
                  }))}
            {finalIsSubmitting && (
              <div
                className="spinner-border ms-2"
                style={{ width: '1rem', height: '1rem' }}
              />
            )}
          </Button>
          {props.isInDraftState && (
            <AutoSaveInfoBox
              className={buttonClassName + ' ms-3'}
              // @ts-expect-error
              onDiscardDraft={props.discardDraft}
              disabled={props.disabled || finalIsSubmitting}
            />
          )}
        </span>
        {props.disabled && propsDisabledHoverText && (
          <UncontrolledPopover
            placement="bottom"
            trigger="hover"
            target={uniqueLocationString + '-save-button'}
          >
            <PopoverBody className="text-dark">
              {propsDisabledHoverText}
            </PopoverBody>
          </UncontrolledPopover>
        )}
      </>
    ),
    [
      uniqueLocationString,
      props.autoFocusSubmitButton,
      props.disabled,
      props.draftFetchState,
      props.buttonColor,
      props.buttonType,
      buttonIsBlock,
      props.submitText,
      props.isInDraftState,
      props.discardDraft,
      buttonClassName,
      finalIsSubmitting,
      handleSubmit,
      formatMessage,
      propsDisabledHoverText,
    ]
  );

  const defaultRenderForm = useCallback(
    (children, button, draftWarning) => {
      return (
        <>
          {children}
          {!props.hideSubmitButton && button}
          {draftWarning}
        </>
      );
    },
    [props.hideSubmitButton]
  );

  const draftWarning = useMemo(
    () => (
      <>
        {props.draftFetchState === 'LOADING' && (
          <span className="ps-3">
            <Spinner className="" spinnerStyle={{ top: undefined }}>
              <span>
                <FormattedMessage
                  id="app.views.widgets.forms.confirm_form.draft.loading"
                  defaultMessage="Loading draft data..."
                />
              </span>
            </Spinner>
          </span>
        )}
      </>
    ),
    [props.draftFetchState]
  );

  const renderForm = props.renderForm ? props.renderForm : defaultRenderForm;

  const handleOnSubmitWithDialog = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      if (propsOnValidate) {
        // if validation fails, stop here
        if (!propsOnValidate(propsObject)) {
          return;
        }
        // otherwise, show the confirmation dialog
        toggleConfirmationDialog();
      }
    },
    [propsObject, propsOnValidate, toggleConfirmationDialog]
  );

  useEffect(() => {
    if (message && propsShowErrorsAsToasts) {
      // @ts-expect-error
      toast.error(propsRenderValidationErrors(message));
    }
  }, [message, propsRenderValidationErrors, propsShowErrorsAsToasts]);

  return (
    <>
      {props.confirmationDialog && (
        <ConfirmationDialogModal
          isOpen={confirmationDialogIsOpen}
          toggle={toggleConfirmationDialog}
          confirmCallback={handleSubmit}
          // @ts-expect-error
          confirmationButtonColor={props.confirmationDialog?.buttonColor}
          // @ts-expect-error
          title={props.confirmationDialog?.title}
          // @ts-expect-error
          description={props.confirmationDialog?.description}
          // @ts-expect-error
          confirmText={props.confirmationDialog?.confirmText}
          // need to pass along validation errors to avoid issue
          // where a 400 causes the dialog to hang
          validationErrors={message}
        />
      )}
      <Form
        role={props.role}
        aria-label={props.ariaLabel}
        noValidate={props.noValidate ? true : undefined}
        className={props.className}
        ref={props.formRef}
        onSubmit={
          props.confirmationDialog ? handleOnSubmitWithDialog : handleSubmit
        }
      >
        {!props.inlineSubmitButton &&
          renderForm(props.children, submitButton, draftWarning)}
        {props.inlineSubmitButton && (
          <Row>
            <Col>{props.children}</Col>
            {!props.hideSubmitButton && (
              <Col className="col-auto ps-0">{submitButton}</Col>
            )}
          </Row>
        )}
      </Form>
      {(isError || message) &&
        !props.showErrorsAsToasts &&
        // @ts-expect-error
        renderValidationErrors(
          <div className={`text-center pt-2 message ${isError && 'error'}`}>
            <span className="text-danger">{message}</span>
          </div>
        )}
    </>
  );
};

const ConfirmForm_propTypes = {
  ariaLabel: PropTypes.string,
  role: PropTypes.string,
  className: PropTypes.string,
  formRef: PropTypes.object,
  preSubmit: PropTypes.func,
  callback: PropTypes.func,
  onValidate: PropTypes.func,
  url: PropTypes.string,
  action: PropTypes.string,
  submitPromise: PropTypes.func,
  method: PropTypes.string,
  object: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  currentOrganizationId: PropTypes.number,
  submitText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  inlineSubmitButton: PropTypes.bool,
  hideSubmitButton: PropTypes.bool,
  buttonIsBlock: PropTypes.bool,
  buttonColor: PropTypes.string,
  buttonClassName: PropTypes.string,
  buttonType: PropTypes.string,
  disabledHoverText: PropTypes.string,
  transformObjectBeforeSubmit: PropTypes.func,
  renderForm: PropTypes.func,
  renderValidationErrors: PropTypes.func,
  doNotSendOrganization: PropTypes.bool,
  showErrorsAsToasts: PropTypes.bool,
  confirmationDialog: PropTypes.object,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onIsSubmittingChange: PropTypes.func,
  // ignores browser's native validation
  noValidate: PropTypes.bool,
  isInDraftState: PropTypes.bool,
  discardDraft: PropTypes.func,
  draftFetchState: PropTypes.string,
  autoFocusSubmitButton: PropTypes.bool,
};

type Props = PropTypes.InferProps<typeof ConfirmForm_propTypes>;

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization } = state;

  return {
    currentOrganizationId: currentOrganization?.id,
  };
};

export default connect(mapStateToProps)(React.memo(ConfirmForm));
